import { ErrorStatus } from "components/ErrorStatus/ErrorStatus"
import { Modal, type ModalProps } from "components/Modal/Modal"
import { useNotification } from "components/Notification/Notification"
import {
	Enrollment,
	UseMutationResultEnrollmentToggle,
} from "interface/enrollment"
import { useCallback } from "react"

export const ToggleEnrollment = ({
	enrollment,
	mutate,
	isPending,
	isError,
	action = "cancel",
	onCancel,
	onSuccess,
	...props
}: ModalProps &
	Partial<UseMutationResultEnrollmentToggle> & {
		enrollment: Enrollment
		action?: "cancel" | "uncancel"
		onSuccess: VoidFunction
		onCancel: VoidFunction
	}) => {
	const { notify } = useNotification()
	const verb = action === "cancel" ? "Cancel" : "Restore"
	const title = `Do you want to ${verb.toLowerCase()} this enrollment?`
	const okText = `${verb} enrollment`
	const cancelText = `Keep enrollment ${action === "cancel" ? "active" : "cancelled"}`
	const bodyText =
		action === "cancel"
			? `If you cancel the enrollment, the patient ${enrollment.fullName} will not receive an Empo Footprint.`
			: `Are you sure you want to restore patient ${enrollment.fullName}'s enrollment?`
	const errorText = `An unexpected error occurred while ${verb.toLowerCase()}ing patient ${enrollment.fullName}'s enrollment. Please, try again.`
	const handleCancelSuccess = useCallback(() => {
		const description = `Patient ${enrollment.fullName}'s enrollment was ${verb.toLowerCase()}ed successfully.`
		mutate?.(enrollment.id, {
			onSuccess: () => {
				notify.success({
					message: okText,
					description,
				})
				onSuccess()
			},
		})
	}, [
		enrollment.fullName,
		enrollment.id,
		mutate,
		notify,
		okText,
		onSuccess,
		verb,
	])

	return (
		<Modal
			{...props}
			okButtonProps={{ danger: action === "cancel" }}
			confirmLoading={isPending}
			title={title}
			okText={okText}
			cancelText={cancelText}
			onOk={handleCancelSuccess}
			onCancel={onCancel}
			classNames={{ body: "flex flex-col gap-2" }}
		>
			{bodyText}
			<ErrorStatus show={isError}>{errorText}</ErrorStatus>
		</Modal>
	)
}
